// =====================================================================================
// FILE: src/services/tableMetadata.js
// =====================================================================================

/**
 * Fetches table metadata from Airtable to get actual table names and field options
 * This allows the UI to display whatever the tables are actually called in Airtable
 * 
 * Example: If you rename "Out of Game" → "Game Timeline" in Airtable,
 * the React app will automatically show "Game Timeline" in the mode tabs
 */

const AIRTABLE_TOKEN = process.env.REACT_APP_AIRTABLE_TOKEN;
const BASE_ID = process.env.REACT_APP_AIRTABLE_BASE_ID;

// Map of table IDs to their mode keys
const TABLE_ID_TO_MODE = {
  [process.env.REACT_APP_AIRTABLE_TABLE_ID]: 'out',
  [process.env.REACT_APP_AIRTABLE_INGAME_TABLE_ID]: 'ingame',
  [process.env.REACT_APP_AIRTABLE_LEDENTITLEMENTS_TABLE_ID]: 'led',
  [process.env.REACT_APP_AIRTABLE_TEAMLIST_TABLE_ID]: 'team',
  [process.env.REACT_APP_AIRTABLE_IPTV_TABLE_ID]: 'iptv',
};

// Map of mode keys to table IDs (reverse lookup)
const MODE_TO_TABLE_ID = {
  'out': process.env.REACT_APP_AIRTABLE_TABLE_ID,
  'ingame': process.env.REACT_APP_AIRTABLE_INGAME_TABLE_ID,
  'led': process.env.REACT_APP_AIRTABLE_LEDENTITLEMENTS_TABLE_ID,
  'team': process.env.REACT_APP_AIRTABLE_TEAMLIST_TABLE_ID,
  'iptv': process.env.REACT_APP_AIRTABLE_IPTV_TABLE_ID,
};

// Fallback names in case metadata fetch fails
const FALLBACK_NAMES = {
  out: 'Out of Game',
  ingame: 'In Game',
  led: 'LED Entitlements',
  team: 'Team List',
  iptv: 'IPTV',
};

const CACHE_KEY = 'venueflow_table_names';
const CATEGORIES_CACHE_KEY = 'venueflow_categories';

// Store full metadata for reuse
let cachedMetadata = null;

/**
 * Get cached table names from sessionStorage
 */
function getCachedTableNames() {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.error('Failed to read cached table names:', e);
  }
  return null;
}

/**
 * Save table names to sessionStorage
 */
function setCachedTableNames(names) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(names));
  } catch (e) {
    console.error('Failed to cache table names:', e);
  }
}

/**
 * Fetches full metadata from Airtable (cached)
 */
async function fetchFullMetadata() {
  if (cachedMetadata) return cachedMetadata;
  
  const response = await fetch(
    `https://api.airtable.com/v0/meta/bases/${BASE_ID}/tables`,
    {
      headers: {
        Authorization: `Bearer ${AIRTABLE_TOKEN}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Metadata API error: ${response.status}`);
  }

  cachedMetadata = await response.json();
  return cachedMetadata;
}

/**
 * Fetches table names from Airtable Metadata API
 * Returns cached names immediately if available, then updates in background
 * @returns {Promise<Object>} Map of mode keys to table names
 */
export async function fetchTableNames() {
  // Return cached names immediately if available
  const cached = getCachedTableNames();
  if (cached) {
    // Fetch fresh data in background to update cache
    fetchFreshTableNames();
    return cached;
  }

  // No cache - fetch and wait
  return fetchFreshTableNames();
}

/**
 * Fetches fresh table names from API and updates cache
 */
async function fetchFreshTableNames() {
  try {
    const data = await fetchFullMetadata();
    const tableNames = {};

    // Map table IDs to their actual names from Airtable
    data.tables.forEach((table) => {
      const mode = TABLE_ID_TO_MODE[table.id];
      if (mode) {
        tableNames[mode] = table.name;
      }
    });

    // Fill in any missing tables with fallback names
    Object.keys(FALLBACK_NAMES).forEach((mode) => {
      if (!tableNames[mode]) {
        tableNames[mode] = FALLBACK_NAMES[mode];
      }
    });

    // Cache the results
    setCachedTableNames(tableNames);
    
    return tableNames;
  } catch (error) {
    console.error('Failed to fetch table metadata:', error);
    // Return fallback names on error
    return FALLBACK_NAMES;
  }
}

/**
 * Fetches category options from a table's Category field
 * Returns the single-select options defined in Airtable
 * 
 * @param {string} mode - The mode key ('out', 'ingame', 'led', 'team', 'iptv')
 * @returns {Promise<string[]>} Array of category names in order
 */
export async function fetchCategoryOptions(mode = 'out') {
  const cacheKey = `${CATEGORIES_CACHE_KEY}_${mode}`;
  
  // Try cache first
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      // Fetch fresh in background
      fetchFreshCategoryOptions(mode, cacheKey);
      return JSON.parse(cached);
    }
  } catch (e) {}
  
  return fetchFreshCategoryOptions(mode, cacheKey);
}

/**
 * Fetches fresh category options from API
 */
async function fetchFreshCategoryOptions(mode, cacheKey) {
  try {
    const data = await fetchFullMetadata();
    const tableId = MODE_TO_TABLE_ID[mode];
    
    // Find the target table
    const table = data.tables?.find(t => t.id === tableId);
    if (!table?.fields) {
      console.warn(`Table not found for mode: ${mode}`);
      return [];
    }
    
    // Find the Category field (single select or multiple select)
    const categoryField = table.fields.find(f => 
      f.name.toLowerCase() === 'category' || 
      f.name.toLowerCase() === 'categories'
    );
    
    if (!categoryField) {
      console.warn(`Category field not found in table: ${table.name}`);
      return [];
    }
    
    // Extract options from single select or multiple select field
    const options = categoryField.options?.choices || [];
    const categoryNames = options.map(opt => opt.name);
    
    console.log(`📋 Loaded ${categoryNames.length} categories for ${mode}:`, categoryNames);
    
    // Cache the results
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(categoryNames));
    } catch (e) {}
    
    return categoryNames;
  } catch (error) {
    console.error('Failed to fetch category options:', error);
    return [];
  }
}

/**
 * Clears the cached table names (useful for testing or forcing refresh)
 */
export function clearTableNameCache() {
  try {
    sessionStorage.removeItem(CACHE_KEY);
    cachedMetadata = null;
  } catch (e) {
    console.error('Failed to clear table name cache:', e);
  }
}

/**
 * Clears the cached categories for a mode
 */
export function clearCategoryCache(mode) {
  try {
    sessionStorage.removeItem(`${CATEGORIES_CACHE_KEY}_${mode}`);
  } catch (e) {
    console.error('Failed to clear category cache:', e);
  }
}
